import { useDebouncedCallback, useTypeaheadProvider } from "@judo/core";
import { useModelLabel } from "@judo/i18n";
import type { Input, TextInput } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { buildIconAdornment } from "../utils/icon-adornment";

export interface TextInputComponentProps {
	element: TextInput | Input;
	readOnly?: boolean;
	value?: string;
	onChange?: (value: string) => void;
	error?: string;
	disabled?: boolean;
	/** JUDO CSS classes (JUDO-viewMode, JUDO-required) for template parity */
	judoClassName?: string;
}

/**
 * Text input component.
 *
 * When the model element has `isTypeAheadField: true` and a matching
 * typeahead provider is registered in CustomizationsConfig, renders
 * as a MUI Autocomplete with freeSolo mode for suggestion support.
 * Otherwise, renders a plain TextField.
 */
export function TextInputComponent({
	element,
	readOnly,
	value = "",
	onChange,
	error,
	disabled,
	judoClassName,
}: TextInputComponentProps) {
	const testId = getInputTestId(element);
	const { label } = useModelLabel(element);
	const textInputElement = element as TextInput;
	const startAdornment = buildIconAdornment(element.icon);

	const isTypeAhead = String(textInputElement.isTypeAheadField) === "true";
	const typeaheadProvider = useTypeaheadProvider(isTypeAhead ? element["xmi:id"] : undefined);

	// Local state for debounced plain text input.
	//
	// Review refactor R1 — race-condition guard:
	//   Without a dirty flag, an external `value` update arriving while the
	//   user is typing (e.g. a refresh from the server firing between keypresses)
	//   would reset `localValue` through the sync-effect below and drop the
	//   typed characters. `isDirtyRef` marks the component as "user is typing,
	//   don't clobber me"; the debounced onChange clears the flag once the
	//   change has actually propagated to the parent.
	const [localValue, setLocalValue] = useState(value);
	const isDirtyRef = useRef(false);
	useEffect(() => {
		if (!isDirtyRef.current) {
			setLocalValue(value);
		}
	}, [value]);
	const debouncedOnChange = useDebouncedCallback((newValue: string) => {
		onChange?.(newValue);
		isDirtyRef.current = false;
	});

	if (typeaheadProvider) {
		return (
			<TypeaheadInput
				label={label}
				testId={testId}
				value={value}
				onChange={onChange}
				error={error}
				disabled={disabled ?? element.disabled}
				required={element.required}
				readOnly={readOnly ?? element.isReadOnly}
				maxLength={textInputElement.maxLength}
				regexp={textInputElement.regexp}
				provider={typeaheadProvider}
				startAdornment={startAdornment}
				judoClassName={judoClassName}
			/>
		);
	}

	return (
		<TextField
			label={label}
			value={localValue}
			onChange={(e) => {
				isDirtyRef.current = true;
				setLocalValue(e.target.value);
				debouncedOnChange(e.target.value);
			}}
			error={!!error}
			helperText={error}
			disabled={disabled ?? element.disabled}
			required={element.required}
			fullWidth
			className={judoClassName}
			data-testid={testId}
			slotProps={{
				input: {
					readOnly: readOnly ?? element.isReadOnly,
					startAdornment,
				},
				htmlInput: {
					maxLength: textInputElement.maxLength,
					pattern: textInputElement.regexp,
				},
				inputLabel: {
					shrink: true,
				},
			}}
		/>
	);
}

// ============================================
// TYPEAHEAD SUB-COMPONENT
// ============================================

interface TypeaheadInputProps {
	label: string;
	testId: string;
	value: string;
	onChange?: (value: string) => void;
	error?: string;
	disabled?: boolean;
	required?: boolean;
	readOnly?: boolean;
	maxLength?: number;
	regexp?: string;
	provider: (text: string) => Promise<string[]>;
	startAdornment?: React.ReactNode;
	judoClassName?: string;
}

function TypeaheadInput({
	label,
	testId,
	value,
	onChange,
	error,
	disabled,
	required,
	readOnly,
	maxLength,
	regexp,
	provider,
	startAdornment,
	judoClassName,
}: TypeaheadInputProps) {
	const [options, setOptions] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);

	// Fetch suggestions with built-in debounce (default 300ms)
	const fetchSuggestions = useDebouncedCallback(async (text: string) => {
		setLoading(true);
		try {
			const results = await provider(text);
			setOptions(results);
		} catch {
			setOptions([]);
		} finally {
			setLoading(false);
		}
	});

	return (
		<Autocomplete
			freeSolo
			options={options}
			loading={loading}
			value={value}
			inputValue={value}
			onInputChange={(_event, newInputValue, reason) => {
				if (reason === "input") {
					onChange?.(newInputValue);
					fetchSuggestions(newInputValue);
				}
			}}
			onChange={(_event, newValue) => {
				// newValue is string | null in freeSolo mode
				onChange?.(typeof newValue === "string" ? newValue : "");
			}}
			disabled={disabled}
			readOnly={readOnly}
			fullWidth
			data-testid={testId}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					error={!!error}
					helperText={error}
					required={required}
					className={judoClassName}
					slotProps={{
						input: {
							...params.InputProps,
							readOnly,
							startAdornment: startAdornment ? (
								<>
									{startAdornment}
									{params.InputProps.startAdornment}
								</>
							) : (
								params.InputProps.startAdornment
							),
							endAdornment: (
								<>
									{loading && <CircularProgress color="inherit" size={20} data-testid={`${testId}::loading`} />}
									{params.InputProps.endAdornment}
								</>
							),
						},
						htmlInput: {
							...params.inputProps,
							maxLength,
							pattern: regexp,
						},
						inputLabel: {
							shrink: true,
						},
					}}
				/>
			)}
		/>
	);
}
